import { Controller, useFormContext } from "react-hook-form";
import { Trash2 } from "lucide-react";
import startCase from "lodash/startCase";

import { Button } from "components/ui/button";
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from "components/ui/combobox";
import { Item, ItemActions, ItemContent } from "components/ui/item";
import { CustomFieldValueEditor } from "components/NewAdvancedSearch/CustomFieldValueEditor";

const groupByModel = fields => {
  const byModel = new Map();
  fields.forEach(field => {
    if (!byModel.has(field.model)) {
      byModel.set(field.model, { value: field.model, items: [] });
    }
    byModel.get(field.model).items.push(field);
  });
  return [...byModel.values()];
};

export function CustomFieldFilterCard({
  index,
  fields = [],
  usedKeys = [],
  onRemove,
}) {
  const { control, setValue, watch } = useFormContext();
  const descriptor = watch(`rules.${index}.field`);
  const groups = groupByModel(fields);

  return (
    <Item variant="muted">
      <ItemContent>
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <Controller
            control={control}
            name={`rules.${index}.field`}
            render={({ field }) => (
              <Combobox
                items={groups}
                value={field.value}
                onValueChange={item => {
                  field.onChange(item);
                  setValue(`rules.${index}.value`, null);
                }}
                itemToStringValue={item => item.key}
                itemToStringLabel={item => item.title}
                isItemEqualToValue={(item, value) => item.key === value?.key}
              >
                <ComboboxInput
                  placeholder="Select a custom field"
                  className="tw:bg-white tw:dark:bg-background tw:w-52 tw:max-w-full"
                />
                <ComboboxContent>
                  <ComboboxEmpty>No custom fields found.</ComboboxEmpty>
                  <ComboboxList>
                    {group => (
                      <ComboboxGroup key={group.value} items={group.items}>
                        <ComboboxLabel>{startCase(group.value)}</ComboboxLabel>
                        <ComboboxCollection>
                          {item => (
                            <ComboboxItem
                              key={item.key}
                              value={item}
                              disabled={
                                usedKeys.includes(item.key) &&
                                item.key !== descriptor?.key
                              }
                            >
                              {item.title}
                            </ComboboxItem>
                          )}
                        </ComboboxCollection>
                      </ComboboxGroup>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            )}
          />
          {descriptor && (
            <Controller
              control={control}
              name={`rules.${index}.value`}
              render={({ field }) => (
                <CustomFieldValueEditor
                  descriptor={descriptor}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </div>
      </ItemContent>
      <ItemActions>
        <Button
          type="button"
          variant="destructive"
          size="icon"
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </ItemActions>
    </Item>
  );
}
